import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { formatCurrency } from '../utils/formatters';
import { 
  Plus, 
  Search, 
  Eye, 
  Edit, 
  Trash2, 
  Send, 
  Download, 
  Check, 
  X, 
  FileText,
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle2,
  ChevronUp,
  ChevronDown,
  AlertTriangle
} from 'lucide-react';

const ProposalsPage = () => {
  const [view, setView] = useState('list'); // list, form, detail
  const [proposals, setProposals] = useState([]);
  const [filteredProposals, setFilteredProposals] = useState([]);
  const [currentProposal, setCurrentProposal] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);

  // Default content
  const defaultServicesDescription = "Cleanifax will complete all cleaning services as described below to ensure each unit or area is turned over in a move-in-ready condition. This scope will be completed over a maximum of two (2) site visits — one Initial Clean and one Final Clean — unless otherwise approved in writing.";
  
  const defaultServicesItems = [
    "Dust and debris removal from all accessible areas",
    "Appliance interiors and exteriors thoroughly cleaned",
    "Cabinet interiors and exteriors wiped and detailed",
    "Windows and patio doors (interior and accessible exterior) cleaned",
    "Bathrooms fully detailed (toilets, showers, tubs, sinks, mirrors, fixtures)",
    "Floors vacuumed and mopped throughout",
    "Sticker and adhesive residue removal from surfaces",
    "Balcony surfaces swept and wiped down"
  ];
  
  const defaultExclusionsItems = [
    "Exterior windows requiring lifts or specialized access equipment",
    "Removal or hauling of construction debris",
    "Specialized stain, paint, or material remediation beyond standard cleaning"
  ];
  
  const defaultCleaningScheduleVisits = "• Visit 1 — Initial Clean: Conducted once major construction work is complete. Focuses on heavy dust removal, debris clearing, and preparation for trade touch-ups and inspections.\n• Visit 2 — Final Clean: Completed after all trades and deficiencies are addressed. Focuses on final detailing, polishing, and presentation for occupancy or handover.";
  
  const defaultCleaningScheduleAdditional = "Any cleaning required beyond the two scheduled visits (e.g., re-cleaning due to ongoing trade activity, damage, or post-inspection work) will be billed separately upon client approval.";

  const defaultTermsConditions = "This estimate is valid for 30 days from the date of issue. The estimate covers only the services listed. Additional work will be charged separately. The client must ensure safe and reasonable site access (electricity, water, disposal facilities). Cleanifax staff must follow site safety protocols; client to inform of special requirements. Cleanifax Inc. is covered by WCB Nova Scotia and $2M Liability Insurance. A final walkthrough will be conducted; extra cleaning requested after acceptance will be billed. Payment terms: 25% upon signing the contract, and the remaining upon completion of the work. Jobs canceled with less than 48 hours' notice may incur up to 25% cancellation fee. Cleanifax is not liable for damages due to pre-existing conditions or unsafe site conditions. These terms are governed by the laws of Nova Scotia.";

  // Form state with default values
  const [formData, setFormData] = useState({
    prepared_for_name: '',
    prepared_for_company: '',
    project_property: '',
    address: '',
    estimate_date: new Date().toISOString().split('T')[0],
    services_scope_description: defaultServicesDescription,
    services_items: [...defaultServicesItems],
    exclusions_items: [...defaultExclusionsItems],
    cleaning_schedule_visits: defaultCleaningScheduleVisits,
    cleaning_schedule_additional: defaultCleaningScheduleAdditional,
    line_items: [{ description: '', quantity_area: '', unit_price: 0, total: 0 }],
    hst_percentage: 15.0,
    terms_conditions: defaultTermsConditions,
  });

  useEffect(() => {
    fetchSettings();
    fetchProposals();
    fetchStats();
    
    // Check if coming from lead
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('fromLead') === 'true') {
      const leadData = sessionStorage.getItem('proposalFromLead');
      if (leadData) {
        const data = JSON.parse(leadData);
        setFormData({
          ...formData,
          ...data,
        });
        setView('form');
        sessionStorage.removeItem('proposalFromLead');
        toast.success('Proposal form pre-filled from lead');
      }
    }
  }, []);

  useEffect(() => {
    filterProposals();
  }, [proposals, searchTerm, statusFilter]);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
      // Update form with default values from settings if available
      setFormData(prev => ({
        ...prev,
        hst_percentage: response.data.default_tax_rate || 15.0,
      }));
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchProposals = async () => {
    try {
      const response = await api.get('/proposals');
      setProposals(response.data);
    } catch (error) {
      console.error('Error fetching proposals:', error);
      toast.error('Failed to fetch proposals');
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/proposals/stats/summary');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const filterProposals = () => {
    let filtered = proposals;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.prepared_for_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.project_property.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.estimate_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.prepared_for_company && p.prepared_for_company.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    setFilteredProposals(filtered);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle editable list operations
  const handleAddListItem = (listName) => {
    setFormData(prev => ({
      ...prev,
      [listName]: [...prev[listName], '']
    }));
  };

  const handleUpdateListItem = (listName, index, value) => {
    setFormData(prev => ({
      ...prev,
      [listName]: prev[listName].map((item, i) => i === index ? value : item)
    }));
  };

  const handleDeleteListItem = (listName, index) => {
    setFormData(prev => ({
      ...prev,
      [listName]: prev[listName].filter((_, i) => i !== index)
    }));
  };

  const handleMoveListItem = (listName, index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= formData[listName].length) return;
    
    setFormData(prev => {
      const newList = [...prev[listName]];
      [newList[index], newList[newIndex]] = [newList[newIndex], newList[index]];
      return { ...prev, [listName]: newList };
    });
  };

  const handleLineItemChange = (index, field, value) => {
    const newLineItems = [...formData.line_items];
    
    // Parse the value to remove any formatting
    let numericValue = value;
    if (typeof value === 'string') {
      numericValue = value.replace(/,/g, '');
    }
    
    newLineItems[index][field] = numericValue;

    // Bidirectional price calculation with divide-by-zero guard
    const quantity = parseFloat(newLineItems[index].quantity_area) || 0;
    
    if (field === 'quantity_area' || field === 'unit_price') {
      // If quantity or unit_price changes → recalculate total
      const unitPrice = parseFloat(newLineItems[index].unit_price) || 0;
      const calculatedTotal = quantity * unitPrice;
      newLineItems[index].total = calculatedTotal.toFixed(2);
    } else if (field === 'total') {
      // If total changes → recalculate unit_price (only if quantity > 0 to avoid divide-by-zero)
      if (quantity > 0) {
        const total = parseFloat(numericValue) || 0;
        const calculatedUnitPrice = total / quantity;
        newLineItems[index].unit_price = calculatedUnitPrice.toFixed(2);
      }
      // If quantity is 0, just update the total value without recalculating unit price
    }

    setFormData(prev => ({ ...prev, line_items: newLineItems }));
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      line_items: [...prev.line_items, { description: '', quantity_area: '', unit_price: 0, total: 0 }]
    }));
  };

  const removeLineItem = (index) => {
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  // Note: Generic list handlers (handleAddListItem, handleUpdateListItem, handleDeleteListItem, handleMoveListItem) are used for all editable lists

  const calculateTotals = () => {
    const subtotal = formData.line_items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
    const hst = subtotal * (formData.hst_percentage / 100);
    const total = subtotal + hst;
    return { subtotal, hst, total };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEditing && currentProposal) {
        await api.put(`/proposals/${currentProposal.id}`, formData);
        toast.success('Proposal updated successfully');
      } else {
        await api.post('/proposals', formData);
        toast.success('Proposal created successfully');
      }
      
      fetchProposals();
      fetchStats();
      resetForm();
      setView('list');
    } catch (error) {
      console.error('Error saving proposal:', error);
      toast.error('Failed to save proposal');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      prepared_for_name: '',
      prepared_for_company: '',
      project_property: '',
      address: '',
      estimate_date: new Date().toISOString().split('T')[0],
      services_scope_description: defaultServicesDescription,
      services_items: [...defaultServicesItems],
      exclusions_items: [...defaultExclusionsItems],
      cleaning_schedule_visits: defaultCleaningScheduleVisits,
      cleaning_schedule_additional: defaultCleaningScheduleAdditional,
      line_items: [{ description: '', quantity_area: '', unit_price: 0, total: 0 }],
      hst_percentage: settings?.default_tax_rate || 15.0,
      terms_conditions: defaultTermsConditions,
    });
    setIsEditing(false);
    setCurrentProposal(null);
  };

  const handleEdit = (proposal) => {
    setCurrentProposal(proposal);
    setFormData({
      prepared_for_name: proposal.prepared_for_name,
      prepared_for_company: proposal.prepared_for_company || '',
      project_property: proposal.project_property,
      address: proposal.address || '',
      estimate_date: proposal.estimate_date,
      services_scope_description: proposal.services_scope_description || defaultServicesDescription,
      services_items: proposal.services_items || [...defaultServicesItems],
      exclusions_items: proposal.exclusions_items || [...defaultExclusionsItems],
      cleaning_schedule_visits: proposal.cleaning_schedule_visits || defaultCleaningScheduleVisits,
      cleaning_schedule_additional: proposal.cleaning_schedule_additional || defaultCleaningScheduleAdditional,
      line_items: proposal.line_items,
      hst_percentage: proposal.hst_percentage,
      terms_conditions: proposal.terms_conditions || defaultTermsConditions,
    });
    setIsEditing(true);
    setView('form');
  };

  const handleDelete = async (proposalId) => {
    // Get proposal details for better confirmation message
    const proposal = proposals.find(p => p.id === proposalId);
    
    let confirmMessage = 'Are you sure you want to delete this proposal?';
    if (proposal?.status === 'accepted' && proposal?.converted_to_invoice_id) {
      confirmMessage = 'WARNING: This proposal is accepted and has a linked invoice. Deleting this proposal will NOT delete the invoice. Are you sure you want to continue?';
    } else if (proposal?.status === 'accepted') {
      confirmMessage = 'This proposal is accepted. Are you sure you want to delete it?';
    }
    
    if (!window.confirm(confirmMessage)) return;

    try {
      await api.delete(`/proposals/${proposalId}`);
      toast.success('Proposal deleted successfully');
      fetchProposals();
      fetchStats();
      if (currentProposal?.id === proposalId) {
        setView('list');
        setCurrentProposal(null);
      }
    } catch (error) {
      console.error('Error deleting proposal:', error);
      toast.error('Failed to delete proposal');
    }
  };

  const handleViewDetail = async (proposal) => {
    setCurrentProposal(proposal);
    setView('detail');
  };

  const handleSendProposal = async (proposalId) => {
    try {
      await api.post(`/proposals/${proposalId}/send`);
      toast.success('Proposal sent successfully');
      fetchProposals();
      fetchStats();
    } catch (error) {
      console.error('Error sending proposal:', error);
      toast.error('Failed to send proposal');
    }
  };

  const handleDownloadPDF = async (proposalId, estimateNumber) => {
    try {
      const response = await api.get(`/proposals/${proposalId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `proposal_${estimateNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    }
  };

  const handleConvertToInvoice = async (proposalId) => {
    if (!window.confirm('Convert this proposal to an invoice?')) return;

    try {
      const response = await api.post(`/proposals/${proposalId}/convert-to-invoice`);
      toast.success(`Invoice ${response.data.invoice_number} created successfully`);
      fetchProposals();
    } catch (error) {
      console.error('Error converting to invoice:', error);
      toast.error(error.response?.data?.detail || 'Failed to convert proposal');
    }
  };

  const handleStatusChange = async (proposalId, newStatus) => {
    try {
      await api.put(`/proposals/${proposalId}/status`, { status: newStatus });
      toast.success(`Proposal status updated to ${newStatus}`);
      fetchProposals();
      if (currentProposal && currentProposal.id === proposalId) {
        setCurrentProposal({ ...currentProposal, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleAcceptProposal = async (proposalId) => {
    if (!window.confirm('Accept this proposal? This will automatically create a project and draft invoice.')) return;

    try {
      // First update status to accepted which creates the project
      const statusResponse = await api.put(`/proposals/${proposalId}/status`, { status: 'accepted' });
      
      if (statusResponse.data.project_id) {
        toast.success(`Proposal accepted! Project created successfully. You can find it in the Projects section.`, { duration: 5000 });
      }
      
      // Then call the accept endpoint which creates the invoice
      const response = await api.post(`/proposals/${proposalId}/accept`, {
        accepted_by_name: "Admin",
        accepted_by_title: "Administrator",
        acceptance_signature: "Admin Approval",
        signature_type: "typed"
      });
      
      fetchProposals();
      fetchStats();
      if (currentProposal && currentProposal.id === proposalId) {
        setCurrentProposal({ ...currentProposal, status: 'accepted' });
      }
    } catch (error) {
      console.error('Error accepting proposal:', error);
      toast.error(error.response?.data?.detail || 'Failed to accept proposal');
    }
  };

  const handleRejectProposal = async (proposalId) => {
    const reason = window.prompt('Rejection reason (optional):');
    
    try {
      await api.post(`/proposals/${proposalId}/reject`, { rejection_reason: reason });
      toast.success('Proposal rejected');
      fetchProposals();
      fetchStats();
      if (currentProposal && currentProposal.id === proposalId) {
        setCurrentProposal({ ...currentProposal, status: 'rejected' });
      }
    } catch (error) {
      console.error('Error rejecting proposal:', error);
      toast.error('Failed to reject proposal');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-800', label: 'Draft' },
      sent: { color: 'bg-blue-100 text-blue-800', label: 'Sent' },
      accepted: { color: 'bg-green-100 text-green-800', label: 'Accepted' },
      rejected: { color: 'bg-red-100 text-red-800', label: 'Rejected' },
      expired: { color: 'bg-yellow-100 text-yellow-800', label: 'Expired' },
    };

    const config = statusConfig[status] || statusConfig.draft;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  // Render List View
  const renderListView = () => (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Proposals</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total_proposals || 0}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Value</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats.total_value || 0)}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Accepted Value</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats.accepted_value || 0)}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Accepted</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.accepted_count || 0}</div>
            <p className="text-xs text-muted-foreground">
              {stats.sent_count || 0} sent | {stats.draft_count || 0} draft
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Proposals</CardTitle>
            <Button onClick={() => { resetForm(); setView('form'); }}>
              <Plus className="h-4 w-4 mr-2" />
              New Proposal
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 mb-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by client, project, or estimate number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Proposals Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Estimate #</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProposals.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                      No proposals found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredProposals.map((proposal) => (
                    <TableRow key={proposal.id}>
                      <TableCell className="font-medium">{proposal.estimate_number}</TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{proposal.prepared_for_name}</div>
                          {proposal.prepared_for_company && (
                            <div className="text-sm text-gray-500">{proposal.prepared_for_company}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{proposal.project_property}</TableCell>
                      <TableCell>{proposal.estimate_date}</TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(proposal.total_estimate)}
                      </TableCell>
                      <TableCell>{getStatusBadge(proposal.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetail(proposal)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {(proposal.status === 'draft' || proposal.status === 'sent') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(proposal)}
                              title={proposal.status === 'sent' ? 'Edit for negotiation' : 'Edit proposal'}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                          {proposal.status === 'draft' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSendProposal(proposal.id)}
                            >
                              <Send className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadPDF(proposal.id, proposal.estimate_number)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          {proposal.status === 'accepted' && !proposal.converted_to_invoice_id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleConvertToInvoice(proposal.id)}
                              className="text-green-600"
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(proposal.id)}
                            className="text-red-600"
                            title="Delete proposal"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Render Form View
  const renderFormView = () => {
    const totals = calculateTotals();

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">
            {isEditing ? 'Edit Proposal' : 'New Proposal'}
          </h2>
          <Button variant="outline" onClick={() => { setView('list'); resetForm(); }}>
            Back to List
          </Button>
        </div>

        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle>Proposal Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Client Information */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="prepared_for_name">Prepared For (Name) *</Label>
                  <Input
                    id="prepared_for_name"
                    name="prepared_for_name"
                    value={formData.prepared_for_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="prepared_for_company">Company</Label>
                  <Input
                    id="prepared_for_company"
                    name="prepared_for_company"
                    value={formData.prepared_for_company}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="project_property">Project/Property *</Label>
                  <Input
                    id="project_property"
                    name="project_property"
                    value={formData.project_property}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="estimate_date">Estimate Date *</Label>
                  <Input
                    id="estimate_date"
                    name="estimate_date"
                    type="date"
                    value={formData.estimate_date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="address">Address</Label>
                <Textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  rows={2}
                />
              </div>

              {/* Services Included Section */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4 text-[#247C75]">1. Services Included (Scope of Work)</h3>
                
                {/* Scope Description */}
                <div className="mb-4">
                  <Label htmlFor="services_scope_description">Scope Description</Label>
                  <Textarea
                    id="services_scope_description"
                    name="services_scope_description"
                    value={formData.services_scope_description}
                    onChange={handleInputChange}
                    rows={3}
                    placeholder="Enter scope description..."
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Brief description and two-visit limit statement
                  </p>
                </div>

                {/* Services Items - Editable List */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <Label>Service Items</Label>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleAddListItem('services_items')}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Service
                    </Button>
                  </div>
                  <div className="space-y-2 border rounded-lg p-4 bg-slate-50">
                    {formData.services_items && formData.services_items.map((service, index) => (
                      <div key={index} className="flex items-center gap-2 bg-white p-2 rounded border">
                        <div className="flex flex-col gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveListItem('services_items', index, 'up')}
                            disabled={index === 0}
                            className="h-6 w-6 p-0"
                          >
                            <ChevronUp className="h-3 w-3" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveListItem('services_items', index, 'down')}
                            disabled={index === formData.services_items.length - 1}
                            className="h-6 w-6 p-0"
                          >
                            <ChevronDown className="h-3 w-3" />
                          </Button>
                        </div>
                        <Input
                          value={service}
                          onChange={(e) => handleUpdateListItem('services_items', index, e.target.value)}
                          placeholder="Enter service item..."
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteListItem('services_items', index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Exclusions Section */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4 text-[#247C75]">2. Exclusions</h3>
                
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <Label>Exclusion Items</Label>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleAddListItem('exclusions_items')}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Exclusion
                    </Button>
                  </div>
                  <div className="space-y-2 border rounded-lg p-4 bg-slate-50">
                    {formData.exclusions_items && formData.exclusions_items.map((exclusion, index) => (
                      <div key={index} className="flex items-center gap-2 bg-white p-2 rounded border">
                        <div className="flex flex-col gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveListItem('exclusions_items', index, 'up')}
                            disabled={index === 0}
                            className="h-6 w-6 p-0"
                          >
                            <ChevronUp className="h-3 w-3" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveListItem('exclusions_items', index, 'down')}
                            disabled={index === formData.exclusions_items.length - 1}
                            className="h-6 w-6 p-0"
                          >
                            <ChevronDown className="h-3 w-3" />
                          </Button>
                        </div>
                        <Input
                          value={exclusion}
                          onChange={(e) => handleUpdateListItem('exclusions_items', index, e.target.value)}
                          placeholder="Enter exclusion item..."
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteListItem('exclusions_items', index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Cleaning Schedule Section */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4 text-[#247C75]">3. Cleaning Schedule</h3>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="cleaning_schedule_visits">Schedule Details</Label>
                    <Textarea
                      id="cleaning_schedule_visits"
                      name="cleaning_schedule_visits"
                      value={formData.cleaning_schedule_visits}
                      onChange={handleInputChange}
                      rows={4}
                      placeholder="Describe the cleaning schedule..."
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Describe the cleaning visits and schedule
                    </p>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-2 mb-2">
                      <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <Label htmlFor="cleaning_schedule_additional" className="text-amber-900 font-semibold">
                        Additional Visits (Important Notice)
                      </Label>
                    </div>
                    <Textarea
                      id="cleaning_schedule_additional"
                      name="cleaning_schedule_additional"
                      value={formData.cleaning_schedule_additional}
                      onChange={handleInputChange}
                      rows={3}
                      placeholder="Additional visit terms..."
                      className="bg-white"
                    />
                    <p className="text-xs text-amber-700 mt-1">
                      Terms for cleaning beyond the two scheduled visits
                    </p>
                  </div>
                </div>
              </div>

              {/* Line Items */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <Label>Description of Services</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addLineItem}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Line Item
                  </Button>
                </div>

                <div className="space-y-4">
                  {formData.line_items.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-4 items-end border p-4 rounded-lg">
                      <div className="col-span-5">
                        <Label>Description</Label>
                        <Input
                          value={item.description}
                          onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                          placeholder="Post-construction cleaning"
                          required
                        />
                      </div>
                      <div className="col-span-3">
                        <Label>Quantity/Area</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={item.quantity_area}
                          onChange={(e) => handleLineItemChange(index, 'quantity_area', e.target.value)}
                          placeholder="5000"
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">Enter numeric value (e.g., 5000 for sq ft)</p>
                      </div>
                      <div className="col-span-2">
                        <Label>Unit Price ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={item.unit_price}
                          onChange={(e) => handleLineItemChange(index, 'unit_price', e.target.value)}
                          placeholder="0.00"
                          required
                        />
                        {parseFloat(item.unit_price) > 0 && (
                          <p className="text-xs text-green-600 mt-1">
                            ${formatCurrency(item.unit_price)}
                          </p>
                        )}
                      </div>
                      <div className="col-span-2">
                        <Label>Total ($)</Label>
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <Input
                              type="number"
                              step="0.01"
                              value={item.total}
                              onChange={(e) => handleLineItemChange(index, 'total', e.target.value)}
                              placeholder="0.00"
                              disabled={!item.quantity_area || parseFloat(item.quantity_area) === 0}
                              className={!item.quantity_area || parseFloat(item.quantity_area) === 0 ? 'bg-gray-100' : ''}
                            />
                            {parseFloat(item.total) > 0 && (
                              <p className="text-xs text-green-600 mt-1">
                                ${formatCurrency(item.total)}
                              </p>
                            )}
                            {(!item.quantity_area || parseFloat(item.quantity_area) === 0) && (
                              <p className="text-xs text-amber-600 mt-1">
                                Enter quantity first
                              </p>
                            )}
                          </div>
                          {formData.line_items.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeLineItem(index)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals Summary */}
              <div className="border-t pt-4">
                {/* HST Percentage Field */}
                <div className="mb-4">
                  <Label htmlFor="hst_percentage">HST % (Tax Rate)</Label>
                  <Input
                    id="hst_percentage"
                    name="hst_percentage"
                    type="number"
                    step="0.1"
                    value={formData.hst_percentage}
                    onChange={handleInputChange}
                    className="w-32"
                  />
                </div>

                <div className="flex justify-end">
                  <div className="w-1/3 space-y-2">
                    <div className="flex justify-between">
                      <span>Subtotal:</span>
                      <span className="font-medium">{formatCurrency(totals.subtotal)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>HST ({formData.hst_percentage}%):</span>
                      <span className="font-medium">{formatCurrency(totals.hst)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t pt-2">
                      <span>Total Estimate:</span>
                      <span>{formatCurrency(totals.total)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Terms & Conditions Section */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4 text-[#247C75]">4. Terms & Conditions</h3>
                
                <div>
                  <Label htmlFor="terms_conditions">Terms & Conditions</Label>
                  <Textarea
                    id="terms_conditions"
                    name="terms_conditions"
                    value={formData.terms_conditions}
                    onChange={handleInputChange}
                    rows={6}
                    placeholder="Enter terms and conditions..."
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Legal terms, payment terms, cancellation policy, liability, etc.
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-4">
                <Button type="button" variant="outline" onClick={() => { setView('list'); resetForm(); }}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Saving...' : (isEditing ? 'Update Proposal' : 'Create Proposal')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    );
  };

  // Render Detail View
  const renderDetailView = () => {
    if (!currentProposal) return null;

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Proposal Details</h2>
            <p className="text-sm text-gray-500 mt-1">Status: {getStatusBadge(currentProposal.status)}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setView('list')}>
              Back to List
            </Button>
            <Button onClick={() => handleDownloadPDF(currentProposal.id, currentProposal.estimate_number)}>
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
            {(currentProposal.status === 'draft' || currentProposal.status === 'sent') && (
              <Button onClick={() => handleEdit(currentProposal)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit {currentProposal.status === 'sent' && '(Negotiation)'}
              </Button>
            )}
            <Button 
              onClick={() => handleDelete(currentProposal.id)}
              variant="destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>

        {/* Status Management Section */}
        {(currentProposal.status === 'sent' || currentProposal.status === 'draft') && (
          <Card className="border-2 border-blue-200 bg-blue-50">
            <CardContent className="pt-6">
              <h3 className="font-semibold mb-4">Manage Proposal Status</h3>
              <div className="flex gap-3">
                <Button 
                  onClick={() => handleAcceptProposal(currentProposal.id)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Check className="h-4 w-4 mr-2" />
                  Accept Proposal
                </Button>
                <Button 
                  onClick={() => handleRejectProposal(currentProposal.id)}
                  variant="destructive"
                >
                  <X className="h-4 w-4 mr-2" />
                  Reject Proposal
                </Button>
                <Button 
                  onClick={() => handleStatusChange(currentProposal.id, 'expired')}
                  variant="outline"
                  className="border-yellow-500 text-yellow-700"
                >
                  <Clock className="h-4 w-4 mr-2" />
                  Mark as Expired
                </Button>
              </div>
              <p className="text-sm text-gray-600 mt-3">
                <strong>Note:</strong> Accepting a proposal will automatically create a draft invoice.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Universal Status Changer */}
        <Card className="border-2 border-gray-200">
          <CardContent className="pt-6">
            <h3 className="font-semibold mb-4">Change Status</h3>
            <div className="flex items-center gap-4">
              <Label>Update status to:</Label>
              <Select 
                value={currentProposal.status} 
                onValueChange={(value) => handleStatusChange(currentProposal.id, value)}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="accepted">Accepted</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-gray-500">Current: {getStatusBadge(currentProposal.status)}</span>
            </div>
            <p className="text-sm text-gray-600 mt-3">
              <strong>Note:</strong> Changing to "Accepted" will NOT auto-create an invoice. Use the "Accept Proposal" button above for that.
            </p>
          </CardContent>
        </Card>

        {/* Show invoice link if converted */}
        {currentProposal.converted_to_invoice_id && (
          <Card className="border-2 border-green-200 bg-green-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-green-800">✓ Invoice Created</h3>
                  <p className="text-sm text-green-600 mt-1">
                    This proposal has been converted to an invoice
                  </p>
                </div>
                <Button 
                  onClick={() => window.location.href = '/invoices'}
                  className="bg-green-600 hover:bg-green-700"
                >
                  View Invoice
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="pt-6" data-testid="proposal-preview">
            {/* Header */}
            <div className="text-center mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_clean-crew-tracker/artifacts/tzw4ofm2_Final_cleanifax_logo_small.png" 
                alt="Cleanifax Logo" 
                className="h-20 w-auto mx-auto mb-4"
              />
              <h1 className="text-2xl font-bold text-[#247C75]">CLEANIFAX INC.</h1>
              <p className="text-lg italic text-[#247C75]">Final Clean for Every Build</p>
              <p className="text-sm mt-2">Halifax, NS, Canada</p>
              <p className="text-sm">Email: info@cleanifax.ca | Phone: (902) 223-7377</p>
              <p className="text-sm">www.cleanifax.ca</p>
            </div>

            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-[#247C75]">ESTIMATE</h2>
            </div>

            {/* Estimate Details */}
            <div className="grid grid-cols-2 gap-y-2 mb-8">
              <div><strong>Estimate Number:</strong></div>
              <div>{currentProposal.estimate_number}</div>
              <div><strong>Date:</strong></div>
              <div>{currentProposal.estimate_date}</div>
              <div><strong>Prepared For:</strong></div>
              <div>
                {currentProposal.prepared_for_name}
                {currentProposal.prepared_for_company && ` / ${currentProposal.prepared_for_company}`}
              </div>
              <div><strong>Project/Property:</strong></div>
              <div>{currentProposal.project_property}</div>
              {currentProposal.address && (
                <>
                  <div><strong>Address:</strong></div>
                  <div>{currentProposal.address}</div>
                </>
              )}
            </div>

            {/* Services Table */}
            <div className="mb-6">
              <h3 className="text-lg font-bold mb-4">Description of Services</h3>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Description</TableHead>
                    <TableHead>Quantity / Area</TableHead>
                    <TableHead className="text-right">Unit Price</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentProposal.line_items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.description}</TableCell>
                      <TableCell>{item.quantity_area}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Totals */}
            <div className="flex justify-end mb-8">
              <div className="w-1/3 space-y-2">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>{formatCurrency(currentProposal.subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span>HST ({currentProposal.hst_percentage}%):</span>
                  <span>{formatCurrency(currentProposal.hst_amount)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Total Estimate:</span>
                  <span>{formatCurrency(currentProposal.total_estimate)}</span>
                </div>
              </div>
            </div>

            {/* Services Included (Scope of Work) */}
            <div className="mb-6">
              <h3 className="text-lg font-bold mb-3 text-[#247C75]">Services Included (Scope of Work)</h3>
              {currentProposal.services_scope_description && (
                <p className="text-sm mb-3 text-gray-700 italic">{currentProposal.services_scope_description}</p>
              )}
              {currentProposal.services_items && currentProposal.services_items.length > 0 && (
                <ul className="list-disc list-inside space-y-1">
                  {currentProposal.services_items.map((item, index) => (
                    <li key={index} className="text-sm">{item}</li>
                  ))}
                </ul>
              )}
            </div>

            {/* Exclusions */}
            <div className="mb-6">
              <h3 className="text-lg font-bold mb-3 text-[#247C75]">Exclusions</h3>
              {currentProposal.exclusions_items && currentProposal.exclusions_items.length > 0 && (
                <ul className="list-disc list-inside space-y-1">
                  {currentProposal.exclusions_items.map((item, index) => (
                    <li key={index} className="text-sm">{item}</li>
                  ))}
                </ul>
              )}
            </div>

            {/* Cleaning Schedule */}
            <div className="mb-6">
              <h3 className="text-lg font-bold mb-3 text-[#247C75]">Cleaning Schedule</h3>
              {currentProposal.cleaning_schedule_visits && (
                <div className="mb-3">
                  <p className="text-sm whitespace-pre-line text-gray-700">{currentProposal.cleaning_schedule_visits}</p>
                </div>
              )}
              {currentProposal.cleaning_schedule_additional && (
                <div className="bg-amber-50 border border-amber-200 rounded p-3">
                  <p className="text-sm font-semibold mb-1 text-amber-900">Additional Visits:</p>
                  <p className="text-sm text-gray-700">{currentProposal.cleaning_schedule_additional}</p>
                </div>
              )}
            </div>

            {/* Terms & Conditions */}
            <div className="mb-8">
              <h3 className="text-lg font-bold mb-3 text-[#247C75]">Terms & Conditions</h3>
              {currentProposal.terms_conditions && (
                <p className="text-sm text-gray-700">{currentProposal.terms_conditions}</p>
              )}
            </div>

            {/* Acceptance Section */}
            <div className="border-t pt-6">
              <p className="font-bold mb-4">Accepted By:</p>
              <div className="space-y-2">
                <div className="border-b pb-1">
                  <span className="text-sm text-gray-500">Signature</span>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <span className="text-sm text-gray-500">Name:</span>
                    <div className="border-b pb-1"></div>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Title:</span>
                    <div className="border-b pb-1"></div>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Date:</span>
                    <div className="border-b pb-1"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Status Badge */}
            <div className="mt-8 flex justify-center">
              {getStatusBadge(currentProposal.status)}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Main Render
  return (
    <div className="container mx-auto">
      {view === 'list' && renderListView()}
      {view === 'form' && renderFormView()}
      {view === 'detail' && renderDetailView()}
    </div>
  );
};

export default ProposalsPage;
